// 应用配置管理
export class AppConfig {
  // 默认配置
  static defaultConfig = {
    app: {
      name: 'AltVerse',
      version: '1.0.0',
      description: 'AI驱动的互动内容创作平台'
    },
    api: {
      baseUrl: 'http://localhost:3001',
      timeout: 30000,
      retryAttempts: 3
    },
    storage: {
      prefix: 'altverse',
      maxSize: 200 * 1024 * 1024, // 200MB
      enableIndexedDB: true
    },
    security: {
      enableCSP: true,
      enableInputValidation: true,
      enableContentFilter: true
    },
    performance: {
      enableLazyLoading: true,
      enableCodeSplitting: true,
      chunkSizeLimit: 1000
    },
    features: {
      enableAnalytics: false,
      enableDebug: true,
      enableSourceMap: true
    }
  }

  // 当前配置
  static currentConfig = { ...this.defaultConfig }

  /**
   * 初始化配置
   */
  static init() {
    // 从环境变量加载配置
    this.loadFromEnv()
    
    // 从本地存储加载用户配置
    this.loadFromStorage()
    
    // 验证配置
    this.validateConfig()
    
    console.log('🔧 应用配置已初始化:', this.currentConfig)
  }

  /**
   * 从环境变量加载配置
   */
  static loadFromEnv() {
    const env = import.meta.env
    
    // 应用配置
    if (env.VITE_APP_NAME) this.currentConfig.app.name = env.VITE_APP_NAME
    if (env.VITE_APP_VERSION) this.currentConfig.app.version = env.VITE_APP_VERSION
    if (env.VITE_APP_DESCRIPTION) this.currentConfig.app.description = env.VITE_APP_DESCRIPTION

    // API配置
    if (env.VITE_API_BASE_URL) this.currentConfig.api.baseUrl = env.VITE_API_BASE_URL
    if (env.VITE_API_TIMEOUT) this.currentConfig.api.timeout = parseInt(env.VITE_API_TIMEOUT)
    if (env.VITE_API_RETRY_ATTEMPTS) this.currentConfig.api.retryAttempts = parseInt(env.VITE_API_RETRY_ATTEMPTS)

    // 存储配置
    if (env.VITE_STORAGE_PREFIX) this.currentConfig.storage.prefix = env.VITE_STORAGE_PREFIX
    if (env.VITE_MAX_STORAGE_SIZE) this.currentConfig.storage.maxSize = this.parseSize(env.VITE_MAX_STORAGE_SIZE)
    if (env.VITE_ENABLE_INDEXEDDB) this.currentConfig.storage.enableIndexedDB = env.VITE_ENABLE_INDEXEDDB === 'true'

    // 安全配置
    if (env.VITE_ENABLE_CSP) this.currentConfig.security.enableCSP = env.VITE_ENABLE_CSP === 'true'
    if (env.VITE_ENABLE_INPUT_VALIDATION) this.currentConfig.security.enableInputValidation = env.VITE_ENABLE_INPUT_VALIDATION === 'true'
    if (env.VITE_ENABLE_CONTENT_FILTER) this.currentConfig.security.enableContentFilter = env.VITE_ENABLE_CONTENT_FILTER === 'true'

    // 性能配置
    if (env.VITE_ENABLE_LAZY_LOADING) this.currentConfig.performance.enableLazyLoading = env.VITE_ENABLE_LAZY_LOADING === 'true'
    if (env.VITE_ENABLE_CODE_SPLITTING) this.currentConfig.performance.enableCodeSplitting = env.VITE_ENABLE_CODE_SPLITTING === 'true'
    if (env.VITE_CHUNK_SIZE_LIMIT) this.currentConfig.performance.chunkSizeLimit = parseInt(env.VITE_CHUNK_SIZE_LIMIT)

    // 功能配置
    if (env.VITE_ENABLE_ANALYTICS) this.currentConfig.features.enableAnalytics = env.VITE_ENABLE_ANALYTICS === 'true'
    if (env.VITE_ENABLE_DEBUG) this.currentConfig.features.enableDebug = env.VITE_ENABLE_DEBUG === 'true'
    if (env.VITE_ENABLE_SOURCE_MAP) this.currentConfig.features.enableSourceMap = env.VITE_ENABLE_SOURCE_MAP === 'true'
  }

  /**
   * 从本地存储加载用户配置
   */
  static loadFromStorage() {
    try {
      const storedConfig = localStorage.getItem(`${this.currentConfig.storage.prefix}-config`)
      if (storedConfig) {
        const userConfig = JSON.parse(storedConfig)
        this.currentConfig = { ...this.currentConfig, ...userConfig }
      }
    } catch (error) {
      console.warn('加载用户配置失败:', error)
    }
  }

  /**
   * 保存用户配置到本地存储
   */
  static saveToStorage() {
    try {
      localStorage.setItem(
        `${this.currentConfig.storage.prefix}-config`,
        JSON.stringify(this.currentConfig)
      )
    } catch (error) {
      console.warn('保存用户配置失败:', error)
    }
  }

  /**
   * 验证配置
   */
  static validateConfig() {
    const errors = []

    // 验证API配置
    if (!this.currentConfig.api.baseUrl) {
      errors.push('API base URL is required')
    }

    // 验证存储配置
    if (this.currentConfig.storage.maxSize <= 0) {
      errors.push('Storage max size must be positive')
    }

    // 验证性能配置
    if (this.currentConfig.performance.chunkSizeLimit <= 0) {
      errors.push('Chunk size limit must be positive')
    }

    if (errors.length > 0) {
      console.error('配置验证失败:', errors)
      throw new Error(`配置验证失败: ${errors.join(', ')}`)
    }
  }

  /**
   * 获取配置值
   * @param {string} path - 配置路径，如 'app.name' 或 'api.timeout'
   * @returns {*} 配置值
   */
  static get(path) {
    return path.split('.').reduce((obj, key) => obj?.[key], this.currentConfig)
  }

  /**
   * 设置配置值
   * @param {string} path - 配置路径
   * @param {*} value - 配置值
   */
  static set(path, value) {
    const keys = path.split('.')
    const lastKey = keys.pop()
    const target = keys.reduce((obj, key) => {
      if (!obj[key]) obj[key] = {}
      return obj[key]
    }, this.currentConfig)
    
    target[lastKey] = value
    this.saveToStorage()
  }

  /**
   * 获取所有配置
   * @returns {Object} 当前配置
   */
  static getAll() {
    return { ...this.currentConfig }
  }

  /**
   * 重置配置到默认值
   */
  static reset() {
    this.currentConfig = { ...this.defaultConfig }
    this.saveToStorage()
  }

  /**
   * 解析大小字符串（如 '200MB'）
   * @param {string} sizeStr - 大小字符串
   * @returns {number} 字节数
   */
  static parseSize(sizeStr) {
    const units = {
      B: 1,
      KB: 1024,
      MB: 1024 * 1024,
      GB: 1024 * 1024 * 1024
    }
    
    const match = sizeStr.match(/^(\d+(?:\.\d+)?)\s*([A-Z]+)$/i)
    if (!match) return parseInt(sizeStr) || 0
    
    const value = parseFloat(match[1])
    const unit = match[2].toUpperCase()
    
    return Math.floor(value * (units[unit] || 1))
  }

  /**
   * 检查是否为开发环境
   * @returns {boolean}
   */
  static isDev() {
    return import.meta.env.DEV
  }

  /**
   * 检查是否为生产环境
   * @returns {boolean}
   */
  static isProd() {
    return import.meta.env.PROD
  }

  /**
   * 获取环境信息
   * @returns {Object} 环境信息
   */
  static getEnvInfo() {
    return {
      mode: import.meta.env.MODE,
      dev: import.meta.env.DEV,
      prod: import.meta.env.PROD,
      version: this.currentConfig.app.version,
      buildTime: import.meta.env.__BUILD_TIME__ || 'unknown'
    }
  }
}

// 自动初始化
AppConfig.init()

export default AppConfig

